import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  weapons,
  armor,
  adventuringGear,
  tools,
  equipmentPacks,
  magicItems,
  formatArmorAC,
  formatRarity,
  type Weapon,
  type Armor,
  type AdventuringGear,
  type Tool,
  type EquipmentPack,
  type MagicItem,
  type MagicItemRarity,
  type MagicItemType,
} from '@character-creator/data';

type EquipmentTab = 'weapons' | 'armor' | 'gear' | 'tools' | 'packs' | 'magic-items';

const TABS: { id: EquipmentTab; label: string }[] = [
  { id: 'weapons', label: 'Weapons' },
  { id: 'armor', label: 'Armor' },
  { id: 'gear', label: 'Gear' },
  { id: 'tools', label: 'Tools' },
  { id: 'packs', label: 'Packs' },
  { id: 'magic-items', label: 'Magic Items' },
];

export function WeaponsGearPage() {
  const [activeTab, setActiveTab] = useState<EquipmentTab>('weapons');
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background-card/95 backdrop-blur-sm border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/create"
              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
            >
              <BackIcon className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-text-primary">Equipment Reference</h1>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${TABS.find((t) => t.id === activeTab)?.label.toLowerCase()}...`}
              className="w-full pl-10 pr-4 py-2 bg-background-secondary border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchQuery('');
                }}
                className={clsx(
                  'px-3 py-1.5 text-sm rounded-lg transition-colors whitespace-nowrap',
                  activeTab === tab.id
                    ? 'bg-accent-gold text-background-primary font-medium'
                    : 'bg-background-elevated text-text-secondary hover:text-text-primary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {activeTab === 'weapons' && <WeaponsSection searchQuery={searchQuery} />}
        {activeTab === 'armor' && <ArmorSection searchQuery={searchQuery} />}
        {activeTab === 'gear' && <GearSection searchQuery={searchQuery} />}
        {activeTab === 'tools' && <ToolsSection searchQuery={searchQuery} />}
        {activeTab === 'packs' && <PacksSection searchQuery={searchQuery} />}
        {activeTab === 'magic-items' && <MagicItemsSection searchQuery={searchQuery} />}
      </main>
    </div>
  );
}

// ============================================================================
// Weapons Section
// ============================================================================

type WeaponCategory = 'all' | 'simple' | 'martial';
type WeaponType = 'all' | 'melee' | 'ranged';

function WeaponsSection({ searchQuery }: { searchQuery: string }) {
  const [category, setCategory] = useState<WeaponCategory>('all');
  const [weaponType, setWeaponType] = useState<WeaponType>('all');

  const filteredWeapons = useMemo(() => {
    return weapons.filter((weapon) => {
      const matchesSearch =
        searchQuery === '' || weapon.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = category === 'all' || weapon.category === category;
      const matchesType = weaponType === 'all' || weapon.type === weaponType;
      return matchesSearch && matchesCategory && matchesType;
    });
  }, [searchQuery, category, weaponType]);

  const groupedWeapons = useMemo(() => {
    const groups: Record<string, Weapon[]> = {};
    filteredWeapons.forEach((weapon) => {
      const key = `${weapon.category}-${weapon.type}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(weapon);
    });
    return groups;
  }, [filteredWeapons]);

  const groupOrder = ['simple-melee', 'simple-ranged', 'martial-melee', 'martial-ranged'];
  const groupLabels: Record<string, string> = {
    'simple-melee': 'Simple Melee Weapons',
    'simple-ranged': 'Simple Ranged Weapons',
    'martial-melee': 'Martial Melee Weapons',
    'martial-ranged': 'Martial Ranged Weapons',
  };

  return (
    <>
      {/* Filters */}
      <div className="flex gap-4 mb-4 overflow-x-auto pb-2">
        <div className="flex gap-1 flex-shrink-0">
          {(['all', 'simple', 'martial'] as const).map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={clsx(
                'px-3 py-1 text-sm rounded-full transition-colors capitalize',
                category === cat
                  ? 'bg-accent-gold text-background-primary'
                  : 'bg-background-elevated text-text-secondary hover:text-text-primary'
              )}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {(['all', 'melee', 'ranged'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setWeaponType(t)}
              className={clsx(
                'px-3 py-1 text-sm rounded-full transition-colors capitalize',
                weaponType === t
                  ? 'bg-accent-gold text-background-primary'
                  : 'bg-background-elevated text-text-secondary hover:text-text-primary'
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-text-muted mb-4">{filteredWeapons.length} weapons found</p>

      {groupOrder.map((groupKey) => {
        const groupWeapons = groupedWeapons[groupKey];
        if (!groupWeapons || groupWeapons.length === 0) return null;

        return (
          <section key={groupKey} className="mb-8">
            <h2 className="text-lg font-semibold text-accent-gold mb-3">{groupLabels[groupKey]}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-text-muted uppercase border-b border-border-default">
                    <th className="py-2 pr-4">Weapon</th>
                    <th className="py-2 pr-4">Damage</th>
                    <th className="py-2">Properties</th>
                  </tr>
                </thead>
                <tbody>
                  {groupWeapons.map((weapon) => (
                    <tr
                      key={weapon.id}
                      className="border-b border-border-default/50 hover:bg-background-elevated/50"
                    >
                      <td className="py-3 pr-4 font-medium text-text-primary">{weapon.name}</td>
                      <td className="py-3 pr-4">
                        <span className="text-text-primary">{weapon.damage}</span>{' '}
                        <span className="text-text-muted text-sm">{weapon.damageType}</span>
                      </td>
                      <td className="py-3">
                        {weapon.properties.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {weapon.properties.map((prop) => (
                              <span
                                key={prop}
                                className="px-2 py-0.5 text-xs rounded bg-background-elevated text-text-secondary capitalize"
                              >
                                {prop}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-text-muted text-sm">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {filteredWeapons.length === 0 && <EmptyState message="No weapons match your criteria." />}

      {/* Property Legend */}
      <section className="mt-12 card p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Weapon Properties</h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <PropertyItem name="Ammunition" description="Requires ammunition to fire." />
          <PropertyItem name="Finesse" description="Use Strength or Dexterity for attack and damage." />
          <PropertyItem name="Heavy" description="Small creatures have disadvantage on attack rolls." />
          <PropertyItem name="Light" description="Ideal for two-weapon fighting." />
          <PropertyItem name="Loading" description="Only one attack per action regardless of extra attacks." />
          <PropertyItem name="Reach" description="Add 5 feet to your reach when attacking." />
          <PropertyItem name="Thrown" description="Can be thrown for a ranged attack." />
          <PropertyItem name="Two-Handed" description="Requires two hands to use." />
          <PropertyItem name="Versatile" description="Can be used one or two-handed for more damage." />
          <PropertyItem name="Special" description="Has unique rules. See weapon description." />
        </div>
      </section>
    </>
  );
}

// ============================================================================
// Armor Section
// ============================================================================

type ArmorCategoryFilter = 'all' | 'light' | 'medium' | 'heavy' | 'shield';

function ArmorSection({ searchQuery }: { searchQuery: string }) {
  const [categoryFilter, setCategoryFilter] = useState<ArmorCategoryFilter>('all');

  const filteredArmor = useMemo(() => {
    return armor.filter((a) => {
      const matchesSearch =
        searchQuery === '' || a.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || a.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, categoryFilter]);

  const groupedArmor = useMemo(() => {
    const groups: Record<string, Armor[]> = {
      light: [],
      medium: [],
      heavy: [],
      shield: [],
    };
    filteredArmor.forEach((a) => {
      groups[a.category].push(a);
    });
    return groups;
  }, [filteredArmor]);

  const categoryLabels: Record<string, string> = {
    light: 'Light Armor',
    medium: 'Medium Armor',
    heavy: 'Heavy Armor',
    shield: 'Shields',
  };

  return (
    <>
      {/* Filters */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
        {(['all', 'light', 'medium', 'heavy', 'shield'] as const).map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setCategoryFilter(cat)}
            className={clsx(
              'px-3 py-1 text-sm rounded-full transition-colors capitalize',
              categoryFilter === cat
                ? 'bg-accent-gold text-background-primary'
                : 'bg-background-elevated text-text-secondary hover:text-text-primary'
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      <p className="text-sm text-text-muted mb-4">{filteredArmor.length} armor items found</p>

      {(['light', 'medium', 'heavy', 'shield'] as const).map((category) => {
        const categoryArmor = groupedArmor[category];
        if (categoryArmor.length === 0) return null;

        return (
          <section key={category} className="mb-8">
            <h2 className="text-lg font-semibold text-accent-gold mb-3">{categoryLabels[category]}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-text-muted uppercase border-b border-border-default">
                    <th className="py-2 pr-4">Armor</th>
                    <th className="py-2 pr-4">AC</th>
                    <th className="py-2 pr-4">Str Req</th>
                    <th className="py-2 pr-4">Stealth</th>
                    <th className="py-2 pr-4">Cost</th>
                    <th className="py-2">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryArmor.map((a) => (
                    <tr
                      key={a.id}
                      className="border-b border-border-default/50 hover:bg-background-elevated/50"
                    >
                      <td className="py-3 pr-4 font-medium text-text-primary">{a.name}</td>
                      <td className="py-3 pr-4 text-text-primary">{formatArmorAC(a)}</td>
                      <td className="py-3 pr-4 text-text-secondary">
                        {a.strengthRequirement ? `Str ${a.strengthRequirement}` : '—'}
                      </td>
                      <td className="py-3 pr-4">
                        {a.stealthDisadvantage ? (
                          <span className="text-status-error text-sm">Disadvantage</span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-text-secondary">{a.cost.amount} gp</td>
                      <td className="py-3 text-text-secondary">{a.weight} lb</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {filteredArmor.length === 0 && <EmptyState message="No armor matches your criteria." />}
    </>
  );
}

// ============================================================================
// Adventuring Gear Section
// ============================================================================

type GearCategoryFilter = 'all' | 'ammunition' | 'arcane-focus' | 'druidic-focus' | 'holy-symbol' | 'container' | 'standard-gear';

function GearSection({ searchQuery }: { searchQuery: string }) {
  const [categoryFilter, setCategoryFilter] = useState<GearCategoryFilter>('all');

  const filteredGear = useMemo(() => {
    return adventuringGear.filter((g) => {
      const matchesSearch =
        searchQuery === '' || g.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || g.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, categoryFilter]);

  const groupedGear = useMemo(() => {
    const groups: Record<string, AdventuringGear[]> = {};
    filteredGear.forEach((g) => {
      if (!groups[g.category]) groups[g.category] = [];
      groups[g.category].push(g);
    });
    return groups;
  }, [filteredGear]);

  const categoryLabels: Record<string, string> = {
    ammunition: 'Ammunition',
    'arcane-focus': 'Arcane Focuses',
    'druidic-focus': 'Druidic Focuses',
    'holy-symbol': 'Holy Symbols',
    container: 'Containers',
    'standard-gear': 'Standard Gear',
  };

  const categoryOrder = ['ammunition', 'arcane-focus', 'druidic-focus', 'holy-symbol', 'container', 'standard-gear'];

  const formatCost = (cost: { amount: number; unit: 'cp' | 'sp' | 'gp' }) => {
    return `${cost.amount} ${cost.unit}`;
  };

  return (
    <>
      {/* Filters */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-2 flex-wrap">
        {(['all', 'ammunition', 'arcane-focus', 'druidic-focus', 'holy-symbol', 'container', 'standard-gear'] as const).map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setCategoryFilter(cat)}
            className={clsx(
              'px-3 py-1 text-sm rounded-full transition-colors whitespace-nowrap',
              categoryFilter === cat
                ? 'bg-accent-gold text-background-primary'
                : 'bg-background-elevated text-text-secondary hover:text-text-primary'
            )}
          >
            {cat === 'all' ? 'All' : categoryLabels[cat]}
          </button>
        ))}
      </div>

      <p className="text-sm text-text-muted mb-4">{filteredGear.length} items found</p>

      {categoryOrder.map((category) => {
        const categoryGear = groupedGear[category];
        if (!categoryGear || categoryGear.length === 0) return null;

        return (
          <section key={category} className="mb-8">
            <h2 className="text-lg font-semibold text-accent-gold mb-3">{categoryLabels[category]}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-text-muted uppercase border-b border-border-default">
                    <th className="py-2 pr-4">Item</th>
                    <th className="py-2 pr-4">Cost</th>
                    <th className="py-2">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryGear.map((g) => (
                    <tr
                      key={g.id}
                      className="border-b border-border-default/50 hover:bg-background-elevated/50"
                    >
                      <td className="py-3 pr-4">
                        <div className="font-medium text-text-primary">{g.name}</div>
                        {g.description && (
                          <div className="text-xs text-text-muted mt-0.5">{g.description}</div>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-text-secondary">{formatCost(g.cost)}</td>
                      <td className="py-3 text-text-secondary">
                        {g.weight > 0 ? `${g.weight} lb` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {filteredGear.length === 0 && <EmptyState message="No gear matches your criteria." />}
    </>
  );
}

// ============================================================================
// Tools Section
// ============================================================================

type ToolCategoryFilter = 'all' | 'artisan-tools' | 'gaming-set' | 'musical-instrument' | 'other-tools';

function ToolsSection({ searchQuery }: { searchQuery: string }) {
  const [categoryFilter, setCategoryFilter] = useState<ToolCategoryFilter>('all');

  const filteredTools = useMemo(() => {
    return tools.filter((t) => {
      const matchesSearch =
        searchQuery === '' || t.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, categoryFilter]);

  const groupedTools = useMemo(() => {
    const groups: Record<string, Tool[]> = {};
    filteredTools.forEach((t) => {
      if (!groups[t.category]) groups[t.category] = [];
      groups[t.category].push(t);
    });
    return groups;
  }, [filteredTools]);

  const categoryLabels: Record<string, string> = {
    'artisan-tools': "Artisan's Tools",
    'gaming-set': 'Gaming Sets',
    'musical-instrument': 'Musical Instruments',
    'other-tools': 'Other Tools',
  };

  const categoryOrder = ['artisan-tools', 'gaming-set', 'musical-instrument', 'other-tools'];

  return (
    <>
      {/* Filters */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
        {(['all', 'artisan-tools', 'gaming-set', 'musical-instrument', 'other-tools'] as const).map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setCategoryFilter(cat)}
            className={clsx(
              'px-3 py-1 text-sm rounded-full transition-colors whitespace-nowrap',
              categoryFilter === cat
                ? 'bg-accent-gold text-background-primary'
                : 'bg-background-elevated text-text-secondary hover:text-text-primary'
            )}
          >
            {cat === 'all' ? 'All' : categoryLabels[cat]}
          </button>
        ))}
      </div>

      <p className="text-sm text-text-muted mb-4">{filteredTools.length} tools found</p>

      {categoryOrder.map((category) => {
        const categoryTools = groupedTools[category];
        if (!categoryTools || categoryTools.length === 0) return null;

        return (
          <section key={category} className="mb-8">
            <h2 className="text-lg font-semibold text-accent-gold mb-3">{categoryLabels[category]}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-text-muted uppercase border-b border-border-default">
                    <th className="py-2 pr-4">Tool</th>
                    <th className="py-2 pr-4">Cost</th>
                    <th className="py-2">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryTools.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-border-default/50 hover:bg-background-elevated/50"
                    >
                      <td className="py-3 pr-4">
                        <div className="font-medium text-text-primary">{t.name}</div>
                        {t.description && (
                          <div className="text-xs text-text-muted mt-0.5 max-w-md">{t.description}</div>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-text-secondary">{t.cost.amount} gp</td>
                      <td className="py-3 text-text-secondary">
                        {t.weight > 0 ? `${t.weight} lb` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {filteredTools.length === 0 && <EmptyState message="No tools match your criteria." />}
    </>
  );
}

// ============================================================================
// Equipment Packs Section
// ============================================================================

function PacksSection({ searchQuery }: { searchQuery: string }) {
  const [expandedPackId, setExpandedPackId] = useState<string | null>(null);

  const filteredPacks = useMemo(() => {
    return equipmentPacks.filter((p) => {
      return searchQuery === '' || p.name.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [searchQuery]);

  const togglePack = (packId: string) => {
    setExpandedPackId((prev) => (prev === packId ? null : packId));
  };

  return (
    <>
      <p className="text-sm text-text-muted mb-4">{filteredPacks.length} equipment packs</p>

      <div className="space-y-3">
        {filteredPacks.map((pack) => (
          <PackCard
            key={pack.id}
            pack={pack}
            isExpanded={expandedPackId === pack.id}
            onToggle={() => togglePack(pack.id)}
          />
        ))}
      </div>

      {filteredPacks.length === 0 && <EmptyState message="No packs match your search." />}
    </>
  );
}

function PackCard({
  pack,
  isExpanded,
  onToggle,
}: {
  pack: EquipmentPack;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="bg-background-card border border-border-default rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-background-elevated transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-text-primary">{pack.name}</h3>
            <p className="text-sm text-text-muted mt-1">{pack.description}</p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-medium text-accent-gold">{pack.cost.amount} gp</div>
            <div className="text-xs text-text-muted">{pack.contents.length} items</div>
          </div>
        </div>
        <div className="flex items-center justify-center mt-2">
          <ChevronIcon className={clsx('w-5 h-5 text-text-muted transition-transform', isExpanded && 'rotate-180')} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border-default">
          <div className="pt-4">
            <h4 className="text-sm font-semibold text-text-primary mb-2">Contents</h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
              {pack.contents.map((item, index) => (
                <li key={index} className="text-sm text-text-secondary">
                  <span className="text-text-primary capitalize">{item.itemId.replace(/-/g, ' ')}</span>
                  {item.quantity > 1 && <span className="text-text-muted"> ({item.quantity})</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Magic Items Section
// ============================================================================

function MagicItemsSection({ searchQuery }: { searchQuery: string }) {
  const [rarityFilter, setRarityFilter] = useState<MagicItemRarity | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<MagicItemType | 'all'>('all');
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const filteredItems = useMemo(() => {
    return magicItems.filter((item) => {
      const matchesSearch =
        searchQuery === '' || item.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRarity = rarityFilter === 'all' || item.rarity === rarityFilter;
      const matchesType = typeFilter === 'all' || item.type === typeFilter;
      return matchesSearch && matchesRarity && matchesType;
    });
  }, [searchQuery, rarityFilter, typeFilter]);

  const toggleItem = (itemId: string) => {
    setExpandedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const rarityColors: Record<MagicItemRarity, string> = {
    common: 'text-text-secondary',
    uncommon: 'text-green-400',
    rare: 'text-blue-400',
    'very-rare': 'text-purple-400',
    legendary: 'text-orange-400',
    artifact: 'text-red-400',
  };

  const typeLabels: Record<MagicItemType, string> = {
    armor: 'Armor',
    potion: 'Potion',
    ring: 'Ring',
    rod: 'Rod',
    scroll: 'Scroll',
    staff: 'Staff',
    wand: 'Wand',
    weapon: 'Weapon',
    'wondrous-item': 'Wondrous Item',
  };

  return (
    <>
      {/* Filters */}
      <div className="space-y-3 mb-4">
        {/* Rarity filter */}
        <div className="flex gap-1 overflow-x-auto pb-2">
          {(['all', 'common', 'uncommon', 'rare', 'very-rare', 'legendary', 'artifact'] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRarityFilter(r)}
              className={clsx(
                'px-3 py-1 text-sm rounded-full transition-colors whitespace-nowrap',
                rarityFilter === r
                  ? 'bg-accent-gold text-background-primary'
                  : 'bg-background-elevated text-text-secondary hover:text-text-primary'
              )}
            >
              {r === 'all' ? 'All Rarities' : formatRarity(r)}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex gap-1 overflow-x-auto pb-2 flex-wrap">
          {(['all', 'armor', 'potion', 'ring', 'rod', 'staff', 'wand', 'weapon', 'wondrous-item'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(t)}
              className={clsx(
                'px-3 py-1 text-sm rounded-full transition-colors whitespace-nowrap',
                typeFilter === t
                  ? 'bg-accent-gold text-background-primary'
                  : 'bg-background-elevated text-text-secondary hover:text-text-primary'
              )}
            >
              {t === 'all' ? 'All Types' : typeLabels[t]}
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-text-muted mb-4">{filteredItems.length} magic items found</p>

      <div className="space-y-2">
        {filteredItems.map((item) => (
          <MagicItemCard
            key={item.id}
            item={item}
            isExpanded={expandedItemId === item.id}
            onToggle={() => toggleItem(item.id)}
            rarityColor={rarityColors[item.rarity]}
            typeLabel={typeLabels[item.type]}
          />
        ))}
      </div>

      {filteredItems.length === 0 && <EmptyState message="No magic items match your criteria." />}
    </>
  );
}

function MagicItemCard({
  item,
  isExpanded,
  onToggle,
  rarityColor,
  typeLabel,
}: {
  item: MagicItem;
  isExpanded: boolean;
  onToggle: () => void;
  rarityColor: string;
  typeLabel: string;
}) {
  return (
    <div className="bg-background-card border border-border-default rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-background-elevated transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-text-primary">{item.name}</h3>
              {item.attunement && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded">
                  Attunement
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-text-muted">
              {typeLabel} &bull; <span className={rarityColor}>{formatRarity(item.rarity)}</span>
            </div>
          </div>
          <ChevronIcon className={clsx('w-5 h-5 text-text-muted transition-transform shrink-0', isExpanded && 'rotate-180')} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border-default">
          <div className="pt-4 space-y-3">
            <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
            {item.attunementRequirement && (
              <p className="text-xs text-text-muted">
                Requires attunement {item.attunementRequirement}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Shared Components
// ============================================================================

function PropertyItem({ name, description }: { name: string; description: string }) {
  return (
    <div>
      <span className="font-medium text-accent-gold">{name}:</span>{' '}
      <span className="text-text-secondary">{description}</span>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <p className="text-text-muted">{message}</p>
    </div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}
